import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useDebounced } from './useDebounced';
import { Skeleton } from './Skeleton';

/**
 * Choose a customer by typing their name.
 *
 * This replaces a `<select>` that rendered every customer in the shop as an `<option>`,
 * fed by a query capped at 500 rows. Two things were wrong with that, and the second one
 * is the worst bug this codebase has had:
 *
 *   1. Scrolling a dropdown of 800 names to find "Henderson" is not choosing, it's hunting.
 *   2. Past the cap the customer was not in the list AT ALL — so a shop with 800 customers
 *      could not create a job, a quote, or an invoice for roughly 300 of them. Not "hard
 *      to find": impossible, with nothing on screen to suggest anything was missing.
 *
 * The search runs in SQL across name, company, email, phone and city, so "555 0134" and
 * "hendersonplumbing.com" both find the right person — which a dropdown never could.
 */
export function CustomerSearchSelect({
  value,
  onChange,
  label = 'Customer',
  autoFocus,
}: {
  /** The chosen customer's id, or '' for nobody yet. */
  value: string;
  onChange: (customerId: string) => void;
  label?: string;
  autoFocus?: boolean;
}) {
  const trpc = useTRPC();
  const [term, setTerm] = useState('');
  const [browsing, setBrowsing] = useState(false);
  const search = useDebounced(term).trim();
  const listId = useId();

  // Who is already chosen — by id, so it works whether or not they'd appear in a search.
  const chosen = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: value }),
    enabled: Boolean(value),
  });

  // A short list, because this is a picker and not a table. Only while the box is open.
  const results = useQuery({
    ...trpc.customer.list.queryOptions({
      search: search || undefined,
      limit: 8,
      offset: 0,
    }),
    enabled: browsing,
    placeholderData: (prev) => prev,
  });

  const field =
    'h-10 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm outline-none focus-visible:border-brand';

  if (value && !browsing) {
    return (
      <div className="block text-xs font-medium">
        <span className="mb-1 block text-muted-foreground">{label}</span>
        <div className="flex items-center justify-between gap-2 rounded-[var(--radius)] border border-border bg-surface px-2 py-2">
          {chosen.isPending ? (
            <Skeleton className="h-4 w-40" />
          ) : (
            <span className="truncate text-sm font-medium">
              {/* A chosen customer whose record has gone is a real state — say so rather
                  than render an empty row that looks like nothing was picked. */}
              {chosen.data?.customer.displayName ??
                'This customer is no longer available'}
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              setBrowsing(true);
              setTerm('');
            }}
            className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-[var(--sw-brand-text)] hover:underline focus-visible:outline focus-visible:outline-2"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="block text-xs font-medium">
      <label className="mb-1 block text-muted-foreground" htmlFor={listId}>
        {label}
      </label>
      <input
        id={listId}
        className={field}
        value={term}
        autoFocus={autoFocus}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => setBrowsing(true)}
        placeholder="Type a name, phone or email…"
        autoComplete="off"
      />

      {browsing ? (
        <div className="mt-1 max-h-56 overflow-y-auto rounded-[var(--radius)] border border-border bg-surface">
          {results.isPending ? (
            <div className="p-2">
              <Skeleton className="h-4 w-32" />
            </div>
          ) : results.isError ? (
            <p className="p-3 text-sm text-[var(--sw-danger-text)]">
              Couldn't load customers.{' '}
              <button
                type="button"
                onClick={() => void results.refetch()}
                className="font-semibold underline"
              >
                Try again
              </button>
            </p>
          ) : results.data.rows.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              {search
                ? `Nobody matches "${search}".`
                : 'No customers yet — add one from the Customers page.'}
            </p>
          ) : (
            <ul>
              {results.data.rows.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(c.id);
                      setBrowsing(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
                  >
                    <span className="block font-medium">{c.displayName}</span>
                    {/* Whatever distinguishes two people with the same name. */}
                    {c.companyName && c.companyName !== c.displayName ? (
                      <span className="block text-xs text-muted-foreground">
                        {c.companyName}
                      </span>
                    ) : c.phone || c.billingCity ? (
                      <span className="block text-xs text-muted-foreground">
                        {[c.phone, c.billingCity].filter(Boolean).join(' · ')}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
              {/* Never imply the eight on screen are all there are. */}
              {results.data.total > results.data.rows.length ? (
                <li className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
                  {results.data.total.toLocaleString()} match — keep typing to narrow it
                  down.
                </li>
              ) : null}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
